import { h, toast } from '../dom';
import { api, type Friend, type User } from '../../api/client';
import { avatar, empty, guard, levelBadge, section, skeleton, streakBadge } from '../components';
import { shell, refreshCounts } from '../shell';
import { navigate, queryParam } from '../router';

type Tab = 'list' | 'requests' | 'search';

export function renderFriends(root: HTMLElement, _me: User) {
  const tab = (queryParam('tab') as Tab) || 'list';
  const content = shell(root, { title: 'Bạn bè', tab: '#/friends' });
  const seg = h('div.seg', {}, ...([['list', 'Bạn bè'], ['requests', 'Lời mời'], ['search', 'Tìm bạn']] as [Tab, string][])
    .map(([t, label]) => h('a.segbtn', { href: `#/friends?tab=${t}`, class: t === tab ? 'on' : '' }, label)));
  content.append(seg, tab === 'list' ? listView() : tab === 'requests' ? requestsView() : searchView());
}

function friendRow(f: Friend, close: Set<string>, onClose: (id: string, on: boolean) => void) {
  const star = h('button.star', {
    class: close.has(f.user.id) ? 'on' : '',
    title: 'Bạn thân (xem được bài "Bạn thân")',
    'aria-label': `Bạn thân: ${f.user.displayName}`,
    'aria-pressed': String(close.has(f.user.id)),
    onclick: (e: Event) => {
      e.preventDefault();
      const on = !close.has(f.user.id);
      onClose(f.user.id, on);
      (e.currentTarget as HTMLElement).classList.toggle('on', on);
      (e.currentTarget as HTMLElement).setAttribute('aria-pressed', String(on));
    },
  }, '★');
  return h('a.urow', { href: `#/u/${f.user.id}` },
    avatar(f.user, 44),
    h('div.umain', {}, h('b', {}, f.user.displayName), h('small', {}, f.user.username ? `@${f.user.username}` : '')),
    streakBadge(f.streak), levelBadge(f.level), star);
}

function listView() {
  return section(async () => ({ friends: await api.friends(), close: new Set(await api.closeFriends()) }), ({ friends, close }) => {
    if (!friends.length)
      return empty('🫂', 'Tìm bạn theo tên hoặc @username, rồi rủ nhau chụp chung nhé.', h('a.primary.btn', { href: '#/friends?tab=search' }, 'Tìm bạn'), 'Chưa có bạn bè');
    let saving = Promise.resolve();
    const toggle = (id: string, on: boolean) => {
      if (on) close.add(id); else close.delete(id);
      saving = saving.then(async () => { await guard(api.setCloseFriends([...close]), on ? 'Đã thêm vào Bạn thân' : 'Đã bỏ khỏi Bạn thân'); });
    };
    return h('div', {},
      h('p.tiny', {}, 'Xếp theo độ thân mật (chỉ bạn thấy). ★ = Bạn thân · 🔥 = chuỗi ngày'),
      h('div.ulist', {}, ...friends.map((f) => friendRow(f, close, toggle))));
  }, () => skeleton('row', 5));
}

function requestsView() {
  return section(() => api.requests(), (r, reload) => {
    const incoming = r.incoming.map((q) => h('div.urow', {},
      h('a', { href: `#/u/${q.from.id}` }, avatar(q.from, 44)),
      h('div.umain', {}, h('b', {}, q.from.displayName), h('small', {}, q.message ?? 'muốn kết bạn với bạn')),
      h('button.primary.small', { onclick: async () => { await guard(api.acceptRequest(q.id), 'Đã thành bạn bè ♡'); reload(); void refreshCounts(true); } }, 'Đồng ý'),
      h('button.secondary.small', { onclick: async () => { await guard(api.declineRequest(q.id)); reload(); } }, 'Xoá')));
    const outgoing = r.outgoing.map((q) => h('div.urow', {},
      h('a', { href: `#/u/${q.to.id}` }, avatar(q.to, 44)),
      h('div.umain', {}, h('b', {}, q.to.displayName), h('small', {}, 'Đang chờ đồng ý')),
      h('button.secondary.small', { onclick: async () => { await guard(api.cancelRequest(q.id)); reload(); } }, 'Huỷ')));
    return h('div', {},
      h('h3', {}, `Lời mời đến (${incoming.length})`),
      incoming.length ? h('div.ulist', {}, ...incoming) : h('p.tiny', {}, 'Không có lời mời mới.'),
      h('h3', {}, `Đã gửi (${outgoing.length})`),
      outgoing.length ? h('div.ulist', {}, ...outgoing) : h('p.tiny', {}, 'Chưa gửi lời mời nào.'));
  }, () => skeleton('row', 3));
}

function searchView() {
  const input = h('input.field', { placeholder: 'Tên hoặc @username', autocapitalize: 'off', type: 'search', 'aria-label': 'Tìm bạn' });
  const results = h('div.ulist');
  let t: ReturnType<typeof setTimeout>;
  // current relations, so results show "Bạn bè ✓" / "Đã gửi" instead of a misleading "Kết bạn"
  const relations = Promise.all([api.friends(), api.requests()]).then(([fr, rq]) => {
    const m = new Map<string, string>();
    fr.forEach((f) => m.set(f.user.id, 'FRIENDS'));
    rq.outgoing.forEach((r) => m.set(r.to.id, 'REQUEST_SENT'));
    rq.incoming.forEach((r) => m.set(r.from.id, 'REQUEST_RECEIVED'));
    return m;
  }).catch(() => new Map<string, string>());
  const run = async () => {
    const q = input.value.trim();
    if (q.length < 2) return results.replaceChildren(h('p.tiny', {}, 'Nhập ít nhất 2 ký tự'));
    try {
      const [users, rel] = await Promise.all([api.search(q), relations]);
      results.replaceChildren(...(users.length ? users.map((u) => userResult(u, rel.get(u.id))) : [h('p.tiny', {}, 'Không tìm thấy ai')]));
    } catch (e) {
      toast((e as Error).message);
    }
  };
  input.oninput = () => { clearTimeout(t); t = setTimeout(run, 300); };
  setTimeout(() => input.focus(), 50);
  return h('div', {}, input, results);
}

function userResult(u: User, relation?: string) {
  const btn = h('button.primary.small', {}, 'Kết bạn');
  if (relation === 'FRIENDS') { btn.textContent = 'Bạn bè ✓'; btn.disabled = true; }
  if (relation === 'REQUEST_SENT') { btn.textContent = 'Đã gửi'; btn.disabled = true; }
  if (relation === 'REQUEST_RECEIVED') btn.textContent = 'Đồng ý';
  btn.onclick = async () => {
    const r = await guard(api.sendRequest(u.id));
    if (r) { btn.textContent = r.status === 'FRIENDS' ? 'Bạn bè ✓' : 'Đã gửi'; btn.disabled = true; }
  };
  return h('div.urow', {},
    h('a', { href: `#/u/${u.id}` }, avatar(u, 44)),
    h('a.umain', { href: `#/u/${u.id}` }, h('b', {}, u.displayName), h('small', {}, u.username ? `@${u.username}` : '')),
    btn);
}

export { navigate };
